// Everything a live-rig bundle's own pose table says about what its decoders can take.
// Notes: ~/comments/aos-threejs-poc/rigConstraints_resolveRigConstraints_one_resolution.md

import { parseMouthController, graftClosedMouth, buildBlinkMacro, poseRow } from "../../lib/rigBoard.js";
import { resolveRigProjection, chooseProjectionScope } from "../rig/mouthSubspace.js";
import { DEFAULT_LIVE_MOUTH_WEIGHT } from "../rig/liveRigRegions.js";
import {
  buildGlobalSubspace, buildRegionalSubspaces, subspaceRetention, trainedLimits,
  buildLiveMouthDrive, resolveEyeFollowDrive, regionKey, arkitToIsaacRig, resolveRigRange,
  type Subspace, type RigClampRange, type LiveMouthDrive, type EyeFollowDrive,
} from "./rigMath.ts";
import { ARKIT_LEN } from "./arkitFrame.ts";
import type { OgsGpuFlags } from "../inference/ogsGpuFlags.ts";

/** The bundle's own trained poses: row 0 plus the whole dense table. */
export interface PoseTable {
  rest: Float32Array;
  presets: Float32Array;
  dim: number;
}

export interface BlinkMacro { applyInto(rig: Float32Array, left: number, right: number): void; count: number; closedRow: number }
export type { Subspace, RigClampRange };

export interface LimitsReport {
  state: "trained" | "declared-range" | "none";
  controls: number | null;
  range: readonly [number, number] | null;
  pad: number | null;
  /** Fraction of the declared range the training data actually covers. */
  covered: number | null;
  requested: string;
}

export interface SubspaceReport {
  state: "applied" | "disabled" | "too-tight" | "none";
  scope: string;
  fellBack: boolean;
  retention: number | null;
  tried: Record<string, number | null> | null;
  components: number | null;
  dims: number | null;
}

export interface RigConstraintReport {
  /** Which rig the geometry was anchored at. */
  anchor: "pose-table row 0" | "ZERO rig (no pose table)";
  /** The trained row the ARKit deltas compose onto, when it is not row 0. */
  arkitBaseRow: number | null;
  poseRows: number;
  limits: LimitsReport;
  subspace: SubspaceReport;
  blink: { count: number; closedRow: number } | null;
  mouth: { source: string; closedRow: number; openRow: number; controls: number; weight: number } | null;
  gaze: { refs: unknown } | null;
  /** What to print, in order. Every refusal is already in here. */
  lines: { level: "info" | "warn" | "error"; text: string }[];
}

export interface RigConstraintSet {
  /** The pose ARKit deltas compose onto — a REAL captured frame whose mouth is closed. */
  arkitBase: Float32Array | null;
  /** Pose-table row 0: the pose whose baked mesh IS `neutral_vertices`. The GEOMETRY anchor. */
  restRig: Float32Array | null;
  /** What the live ARKit path clamps to: per-control trained box, the declared pair, or nothing. */
  range: RigClampRange;
  /** Always the declared pair — the eye aim clamps here, never to the trained box. */
  declaredRange: readonly [number, number] | null;
  blinkMacro: BlinkMacro | null;
  subspace: Subspace | null;
  mouthDrive: LiveMouthDrive | null;
  mouthWeight: number;
  eyeFollow: EyeFollowDrive | null;
  report: RigConstraintReport;
}

export interface ResolveRigConstraintsInput {
  /** Null when the bundle ships no readable pose table — every stage then stands down. */
  poseTable: PoseTable | null;
  rigNames: string[] | null;
  /** The raw scene.json object (mouth_controller, rig_range live there). */
  scene: Record<string, unknown> | null;
  flags: OgsGpuFlags;
  /** ARKit-52 → this control space (rigGatherFromNames). Null before it resolves. */
  gather: ArrayLike<number> | null;
  /** `?livemouth=` etc. Defaults to the live URL when omitted. */
  search?: string;
  /** The live-mouth manifold weight, when the caller has one. */
  mouthWeight?: number;
}

/** One rig per ARKit channel driven to 1 — the expressions the live path is made of. */
function arkitChannelPoses(base: Float32Array | null, gather: ArrayLike<number> | null): Float32Array[] {
  if (!gather || !base?.length) return [];
  const arkit = new Float32Array(ARKIT_LEN);
  const scratch = new Float32Array(188);
  const rig = new Float32Array(base.length);
  const out: Float32Array[] = [];
  for (let channel = 0; channel < ARKIT_LEN; channel++) {
    arkit.fill(0);
    arkit[channel] = 1;
    arkitToIsaacRig(arkit, gather, rig, scratch, base, null);
    out.push(Float32Array.from(rig));
  }
  return out;
}

/** Resolve every constraint this bundle's table supports. Pure; nothing is fetched here. */
export function resolveRigConstraints(input: ResolveRigConstraintsInput): RigConstraintSet {
  const { poseTable, rigNames, scene, flags, gather } = input;
  const search = input.search ?? (typeof window !== "undefined" ? window.location.search : "");
  const lines: RigConstraintReport["lines"] = [];
  const names = rigNames ?? [];

  const restRig = poseTable?.rest ?? null;
  const poseRows = poseTable ? poseTable.presets.length / poseTable.dim : 0;

  // TWO DIFFERENT RESTS, and conflating them left the live mouth unable to close.
  // Row 0 is the GEOMETRY anchor; the pose the ARKit deltas compose onto only has to
  // LOOK like a face at rest, and row 0's mouth is often slightly open.
  const graft = poseTable && restRig
    ? graftClosedMouth({
      spec: parseMouthController(scene?.mouth_controller), pose: restRig,
      presets: poseTable.presets, dim: poseTable.dim,
    })
    : null;
  const arkitBase = graft && poseTable ? poseRow(poseTable.presets, poseTable.dim, graft.row) : restRig;
  if (graft) {
    lines.push({ level: "info", text: `ARKit base: trained pose-table row ${graft.row} `
      + "(the closed-mouth pose whose mouth is nearest row 0); the geometry anchor stays row 0" });
  }

  const declaredRange = resolveRigRange(scene);
  const wantClamp = flags.rigClamp;
  const limits = wantClamp === "trained" && declaredRange && poseTable
    ? trainedLimits(poseTable.presets, poseTable.dim, declaredRange, flags.rigClampPad ?? undefined)
    : null;
  // `none` is the ONLY value that reaches the decoders unclamped, and it says so.
  const range = wantClamp === "none" ? null : (limits ?? declaredRange);
  if (wantClamp !== "trained") {
    lines.push({ level: "warn", text: `?rigclamp=${wantClamp} — the live ARKit path is clamped to `
      + `${wantClamp === "declared" && declaredRange ? `the declared range [${declaredRange.join(", ")}]` : "NOTHING"}, `
      + "not the trained box. A control can now sit multiples past anything the decoders saw." });
  } else if (flags.rigClampPad != null) {
    lines.push({ level: "info", text: `?rigclamppad=${flags.rigClampPad} — trained box widened by that much per side` });
  }
  let covered: number | null = null;
  if (limits && declaredRange) {
    const span = declaredRange[1] - declaredRange[0];
    covered = limits.reduce((sum, pair) => sum + (pair[1] - pair[0]), 0) / (limits.length * span);
    lines.push({ level: "info", text: `rig limits: trained domain on ${limits.length} controls `
      + `(${(100 * covered).toFixed(0)}% of the declared [${declaredRange[0]}, ${declaredRange[1]}] range)` });
  }

  // BLINK AS A CONFIGURATION. ARKit drives one control per eye; every trained blink row
  // moves the whole lid group, so the raw request is off the manifold and the projection
  // halves it. ?blinkmacro=0 reaches a genuinely unconstrained rig.
  const wantBlink = new URLSearchParams(search).get("blinkmacro") !== "0";
  const blinkMacro = poseTable && wantBlink
    ? buildBlinkMacro({ names, presets: poseTable.presets, dim: poseTable.dim })
    : null;
  if (blinkMacro) {
    lines.push({ level: "info", text: `blink macro: ${blinkMacro.count} lid controls driven as a `
      + `configuration from trained row ${blinkMacro.closedRow} (gaze untouched)` });
  }

  const projection = resolveRigProjection(search);
  const buildScope = (scope: string) => {
    if (!poseTable || !projection.enabled) return null;
    const shared = {
      presets: poseTable.presets, rows: poseRows, cols: poseTable.dim,
      extraPoses: arkitBase ? [arkitBase] : [],
    };
    return scope === "global"
      ? buildGlobalSubspace({ ...shared, mouthIdx: Array.from({ length: poseTable.dim }, (_, i) => i) })
      : buildRegionalSubspaces({ ...shared, names, groupBy: regionKey });
  };
  // Each scope is built at most once — a fit is ~140 ms of Jacobi.
  const built = new Map<string, Subspace | null>();
  const fitFor = (scope: string) => {
    if (!built.has(scope)) built.set(scope, buildScope(scope));
    return built.get(scope) ?? null;
  };
  const channelPoses = arkitChannelPoses(arkitBase, gather);
  const measured = new Map<string, { median: number } | null>();
  const retentionOf = (scope: string) => {
    if (!measured.has(scope)) {
      const fit = fitFor(scope);
      measured.set(scope, fit ? subspaceRetention(fit, arkitBase, channelPoses) : null);
    }
    return measured.get(scope)?.median ?? null;
  };
  const chosen = chooseProjectionScope({ scope: projection.scope, explicit: projection.explicit, retentionOf });
  const subspace = chosen.dropped ? null : fitFor(chosen.scope);
  const retention = measured.get(chosen.scope) ?? null;
  if (chosen.dropped) {
    lines.push({ level: "error", text: `rig subspace DISABLED — this bundle's ${poseRows}-row pose table `
      + `spans too few directions to speak through (it keeps ${(100 * (chosen.tried[projection.scope] ?? 0)).toFixed(0)}% `
      + `of the median single-channel ARKit expression; a rich table keeps 80-94%). The rig is now `
      + `UNCONSTRAINED — expect it to over-respond. Re-export with more trained poses for the real fix.` });
  } else if (chosen.fellBack) {
    lines.push({ level: "warn", text: `rig subspace: '${projection.scope}' keeps only `
      + `${(100 * (chosen.tried[projection.scope] ?? 0)).toFixed(0)}% of the median single-channel ARKit `
      + `expression on this ${poseRows}-row table, so the LOOSER 'region' fit is used instead.` });
  } else if (retention) {
    lines.push({ level: "info", text: `rig subspace keeps ${(100 * retention.median).toFixed(0)}% of the median `
      + "single-channel ARKit expression" });
  }

  // The live mouth references are picked against `arkitBase`, NOT row 0: the live rig is
  // composed onto the base, so row 0 chooses references for a face the live path never shows.
  const mouthDrive = poseTable && arkitBase
    ? buildLiveMouthDrive({
      scene, names, presets: poseTable.presets, dim: poseTable.dim, rest: arkitBase,
    })
    : null;
  const mouthWeight = input.mouthWeight ?? DEFAULT_LIVE_MOUTH_WEIGHT;
  if (mouthDrive) {
    lines.push({ level: "info", text: `live mouth drive ready (${mouthDrive.source}): refs `
      + `${mouthDrive.closedRow}->${mouthDrive.openRow}, ${mouthDrive.subset.length} controls, `
      + `manifold weight ${mouthWeight}` });
  }

  // The eye aim clamps to the DECLARED range, never the trained box: isaac's trained data
  // freezes the eye controls, so the trained box has zero width there and would erase the aim.
  const eyeFollow = resolveEyeFollowDrive({
    rigNames: names,
    presets: poseTable?.presets ?? null,
    dim: poseTable?.dim ?? 0,
    eyesIdx: (scene?.mouth_controller as { eyes_idx?: unknown } | undefined)?.eyes_idx ?? null,
    base: arkitBase,
  });

  lines.push({ level: "info", text: `deform anchor: ${restRig ? "pose-table row 0" : "ZERO rig (no pose table)"}` });

  const report: RigConstraintReport = {
    anchor: restRig ? "pose-table row 0" : "ZERO rig (no pose table)",
    arkitBaseRow: graft?.row ?? null,
    poseRows,
    limits: {
      state: limits ? "trained" : (range ? "declared-range" : "none"),
      controls: limits?.length ?? null,
      range: declaredRange,
      pad: flags.rigClampPad ?? null,
      covered,
      requested: wantClamp,
    },
    subspace: {
      state: !projection.enabled ? "disabled" : chosen.dropped ? (retention ? "too-tight" : "none") : "applied",
      scope: chosen.scope,
      fellBack: !!chosen.fellBack,
      retention: retention ? +retention.median.toFixed(3) : null,
      tried: chosen.tried ?? null,
      components: subspace?.k ?? null,
      dims: subspace?.dims ?? null,
    },
    blink: blinkMacro ? { count: blinkMacro.count, closedRow: blinkMacro.closedRow } : null,
    mouth: mouthDrive
      ? {
        source: mouthDrive.source, closedRow: mouthDrive.closedRow,
        openRow: mouthDrive.openRow, controls: mouthDrive.subset.length, weight: mouthWeight,
      }
      : null,
    gaze: eyeFollow ? { refs: eyeFollow.refs ?? null } : null,
    lines,
  };
  // No pose table: every stage that needs trained rows stands down, but the declared
  // range and the eye-follow slots do not need one.
  if (!poseTable) {
    return {
      arkitBase: null, restRig: null, range, declaredRange, blinkMacro: null,
      subspace: null, mouthDrive: null, mouthWeight: 0, eyeFollow, report,
    };
  }
  return {
    arkitBase, restRig, range, declaredRange, blinkMacro, subspace,
    mouthDrive, mouthWeight, eyeFollow, report,
  };
}
